import {
    deviceConfigurationService,
    getVendorBonusEnabled
} from '@/services/deviceConfigurationService'
import { HttpService } from '@/services/httpService'
import { Auth } from '@/types/common/httpRequest'
import {
    DepositBonusEligibilityResponse,
    DepositBonusPromotionsResponse,
    MintDepositBonusRewardResponse
} from '@/types/pos/depositBonus'

const api = {
    /** Anonymous catalog of currently active vendor bonus campaigns. */
    promotions: '/common/deposit-bonus/promotions',
    /** Same list, authenticated, for the kiosk / cashier-app. */
    kioskPromotions: '/cashier-app/deposit-bonus/promotions',
    kioskEligibility:
        '/cashier-app/deposit-bonus/vendor/{walletId}/{identifier}/eligibility',
    publicEligibility:
        '/common/deposit-bonus/vendor/{walletId}/{identifier}/eligibility',
    mintReward: '/common/deposit-bonus/reward/voucher'
}

const emptyPromotionsResponse: DepositBonusPromotionsResponse = {
    promotions: []
}

const isVendorBonusEnabled = () =>
    getVendorBonusEnabled(deviceConfigurationService.getConfiguration())

const withWalletQuery = (path: string, walletId?: number) => {
    if (walletId == null) return path
    return `${path}?walletId=${encodeURIComponent(String(walletId))}`
}

const getActivePromotions = async (
    walletId?: number
): Promise<DepositBonusPromotionsResponse> => {
    if (!isVendorBonusEnabled()) {
        return emptyPromotionsResponse
    }

    return HttpService.Get<DepositBonusPromotionsResponse>(
        withWalletQuery(api.promotions, walletId)
    )
}

const getKioskPromotions = async (
    walletId?: number
): Promise<DepositBonusPromotionsResponse> => {
    if (!isVendorBonusEnabled()) {
        return emptyPromotionsResponse
    }

    return HttpService.Get<DepositBonusPromotionsResponse>(
        withWalletQuery(api.kioskPromotions, walletId),
        Auth.Cashier
    )
}

const eligibilityPath = (template: string, walletId: number, identifier: string) =>
    template
        .replace('{walletId}', encodeURIComponent(String(walletId)))
        .replace('{identifier}', encodeURIComponent(identifier))

const checkKioskEligibility = async (
    walletId: number,
    identifier: string
): Promise<DepositBonusEligibilityResponse> => {
    if (!isVendorBonusEnabled()) {
        throw new Error('Vendor bonus is disabled by device configuration')
    }

    return HttpService.Get<DepositBonusEligibilityResponse>(
        eligibilityPath(api.kioskEligibility, walletId, identifier),
        Auth.Cashier
    )
}

const checkPublicEligibility = async (
    walletId: number,
    identifier: string,
    voucherCode: string
): Promise<DepositBonusEligibilityResponse> => {
    if (!isVendorBonusEnabled()) {
        throw new Error('Vendor bonus is disabled by device configuration')
    }

    const path = eligibilityPath(api.publicEligibility, walletId, identifier)
    return HttpService.Get<DepositBonusEligibilityResponse>(
        `${path}?voucherCode=${encodeURIComponent(voucherCode)}`
    )
}

const mintRewardVoucher = async (
    rewardVoucherToken: string
): Promise<MintDepositBonusRewardResponse> => {
    if (!isVendorBonusEnabled()) {
        throw new Error('Vendor bonus is disabled by device configuration')
    }

    return HttpService.Post<MintDepositBonusRewardResponse>(api.mintReward, {
        rewardVoucherToken
    })
}

export const depositBonusService = {
    getActivePromotions,
    getKioskPromotions,
    checkKioskEligibility,
    checkPublicEligibility,
    mintRewardVoucher
}
